import type { Node } from "@xyflow/react";
import type { BoardNodeData, StickyNoteColor } from "../types/board";

export const STICKY_COLORS: Record<StickyNoteColor, { bg: string; border: string }> = {
  yellow: { bg: "#fef9c3", border: "#eab308" },
  pink: { bg: "#fce7f3", border: "#ec4899" },
  blue: { bg: "#dbeafe", border: "#3b82f6" },
  green: { bg: "#dcfce7", border: "#22c55e" },
  purple: { bg: "#f3e8ff", border: "#a855f7" },
};

export interface NodeFormattingDefaults {
  bgColor: string;
  fontSize: number;
}

type FillField = "bgColor" | "color" | null;
interface FormattingCapabilities {
  fill: FillField;
  fontSize: boolean;
}

const capabilities: Record<BoardNodeData["type"], FormattingCapabilities> = {
  rectangle: { fill: "bgColor", fontSize: true },
  circle: { fill: "bgColor", fontSize: true },
  diamond: { fill: "bgColor", fontSize: true },
  hexagon: { fill: "bgColor", fontSize: true },
  stickyNote: { fill: "color", fontSize: true },
  text: { fill: null, fontSize: true },
  orgChartCard: { fill: "bgColor", fontSize: false },
};

export function getNodeCapabilities(type: string | undefined): FormattingCapabilities | null {
  return type && Object.prototype.hasOwnProperty.call(capabilities, type)
    ? capabilities[type as BoardNodeData["type"]]
    : null;
}

export function isStickyColor(value: string): value is StickyNoteColor {
  return Object.prototype.hasOwnProperty.call(STICKY_COLORS, value);
}

export type FormattingChange =
  | { kind: "fill"; value: string }
  | { kind: "fontSize"; value: number };

/** Supported patches preserve unknown fields, including unsupported legacy formatting data. */
export function patchNodeFormatting(
  type: string | undefined,
  data: Record<string, unknown>,
  change: FormattingChange,
): Record<string, unknown> {
  const support = getNodeCapabilities(type);
  if (change.kind === "fontSize") {
    return support?.fontSize ? { ...data, fontSize: change.value } : data;
  }
  if (!support?.fill || (support.fill === "color" && !isStickyColor(change.value))) return data;
  return { ...data, [support.fill]: change.value };
}

interface FormattingState extends FormattingCapabilities {
  selectedNode: Node | null;
  color: string;
  size: number;
  fillReason?: string;
  fontReason?: string;
}

export function getFormattingState(nodes: readonly Node[], defaults: NodeFormattingDefaults): FormattingState {
  const selected = nodes.filter((node) => node.selected);
  if (selected.length === 0) {
    return { selectedNode: null, fill: "bgColor", fontSize: true, color: defaults.bgColor, size: defaults.fontSize };
  }
  const node = selected.length === 1 ? selected[0] : null;
  const support = getNodeCapabilities(node?.type);
  if (!node || !support) {
    const reason = "Select a single supported node to format.";
    return { selectedNode: null, fill: null, fontSize: false, color: "", size: 14, fillReason: reason, fontReason: reason };
  }
  const data = node.data;
  let color = typeof data.bgColor === "string" ? data.bgColor : "";
  if (support.fill === "color") {
    color = typeof data.color === "string" && isStickyColor(data.color) ? data.color : "yellow";
  } else if (node.type === "orgChartCard") {
    color ||= "#3b82f6";
  }
  return {
    selectedNode: node,
    ...support,
    color,
    size: typeof data.fontSize === "number" && data.fontSize ? data.fontSize : 14,
    fillReason: support.fill ? undefined : "Text nodes have no fill color.",
    fontReason: support.fontSize ? undefined : "Org chart cards use fixed text sizes.",
  };
}
